import type { HttpService } from '@empathyco/playboard-core-domain'
import type { FieldValueAdapter } from '../adapter/interfaces'
import type { PlatformFieldValueEntity } from '../adapter/platform-field-value.adapter'
import type { FieldValue } from '../models'
import type { ContextProvider } from '../providers/interfaces'
import type { FieldValuesService, FieldValuesServiceFetchParams } from './interfaces'
import { interpolate } from '@empathyco/x-adapter'
import { inject, injectable, named, optional } from 'inversify'
import { TYPES } from '../config/inversify.types'

@injectable()
export class PlatformFieldValuesService implements FieldValuesService {
  public constructor(
    @inject(TYPES.HttpService) protected httpService: HttpService<PlatformFieldValueEntity>,
    @inject(TYPES.FieldValueAdapter)
    protected adapter: FieldValueAdapter<PlatformFieldValueEntity | undefined>,
    @inject(TYPES.FacetsUrl) protected url: string,
    @inject(TYPES.ContextProvider) protected contextProvider: ContextProvider,
    @inject(TYPES.Endpoint)
    @named('fieldValues')
    @optional()
    protected endpoint = {
      fieldValues: '/query/{instance}/field-values',
      fieldValuesCategories: '/query/{instance}/field-values-categories',
    },
  ) {}

  async fetch(params: FieldValuesServiceFetchParams): Promise<Array<FieldValue>> {
    try {
      const response = await this.httpService.get<{
        attributes: { content: Array<{ name: string; values: Array<PlatformFieldValueEntity> }> }
      }>(this.buildRequestUrl({}, params.isCategory), {
        parameters: {
          attribute: params.attribute,
          ...params.pivots,
        },
      })
      return (
        response.data?.attributes.content[0]?.values.map(value => this.adapter.decode(value)) ?? []
      )
    } catch (error) {
      throw this.adapter.decodeError(error)
    }
  }

  private buildRequestUrl(parameters: Record<string, any> = {}, isCategory: boolean): string {
    const url = `${this.url}${isCategory ? this.endpoint.fieldValuesCategories : this.endpoint.fieldValues}`
    const { instanceId: instance } = this.contextProvider()
    if (!instance) {
      throw new Error('instance is not defined')
    }
    return interpolate(url, { instance, ...parameters })
  }
}
